import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { AppBar, Autocomplete, Dialog, Grid, IconButton, Toolbar, Typography } from "@mui/material";
import DecreaseButton from "components/DecreaseButton";
import IncreaseButton from "components/IncreaseButton";
import { PartAutocomplete } from "components/parts/PartAutocomplete";
import PrimaryButton from "components/PrimaryButton";
import SecondaryButton from "components/SecondaryButton";
import { StyledContainer } from "components/StyledContainer";
import StyledTextField from "components/StyledTextField";
import { StockSync } from "models/StockSync";
import { PartType, TechnicianType } from "operations/schema/schema";
import { FC, useCallback, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import { addSnackbarMessage } from "store/slices/snackbar.store";
import { updateStockToSync } from "store/slices/stock.store";

interface SelectStockDialogProps {
  open: boolean;
  handleClose: () => void;
  setOpenAddPartDialog: (open: boolean) => void;
  loading: boolean;
  moveStock: boolean;
}

export const SelectStockToSyncDialog: FC<SelectStockDialogProps> = (props) => {
  const { open, handleClose, loading, setOpenAddPartDialog, moveStock } = props;
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { techStock } = useAppSelector((state) => state.stock);
  const { technicians } = useAppSelector((s) => s.cache);
  const [selectedPart, setSelectedPart] = useState<PartType | null>(null);
  const [tempQty, setTempQty] = useState<number>(moveStock ? 1 : 0);
  const [qtyError, setQtyError] = useState<string>("");
  const [partSearch, setPartSearch] = useState<string>("");
  const [selectedTechnician, setSelectedTechnician] = useState<TechnicianType | null>(null);

  const changeSelectedPart = useCallback(
    (selectedPart: PartType | null) => {
      setSelectedPart(selectedPart);
      if (selectedPart !== null && !moveStock) {
        setTempQty(selectedPart.quantity ?? 0);
      } else {
        setTempQty(0);
      }
    },
    [moveStock],
  );

  const changeQtyFromInput = useCallback(
    (value: string) => {
      const numericValue = parseInt(value);
      const maxLimit = selectedPart?.quantity ?? 0;

      if (numericValue <= maxLimit) {
        setTempQty(numericValue);
        setQtyError("");
      } else {
        if (numericValue > maxLimit) {
          setQtyError(
            intl.formatMessage(
              {
                id: "part.quantityTooHigh",
              },
              {
                qty: maxLimit,
              },
            ),
          );
        }
      }
    },
    [intl, selectedPart?.quantity],
  );

  const addStockCb = useCallback(() => {
    if (!selectedPart || !selectedPart.articleNo) return;

    const stockSync: StockSync = {
      part: selectedPart,
      sync: {
        articleNo: selectedPart.articleNo,
        quantity: tempQty,
        moveToTechnicianGuid: moveStock ? selectedTechnician?.guid : null,
      },
      moveToTech: moveStock ? selectedTechnician : null,
    };

    dispatch(updateStockToSync({ stock: stockSync, moveStock }));

    setSelectedPart(null);
    dispatch(
      addSnackbarMessage({
        key: "AddStock-success",
        argument: selectedPart.articleNo ?? "",
      }),
    );
  }, [dispatch, moveStock, tempQty, selectedPart, selectedTechnician]);

  const closeDialog = useCallback(() => {
    setOpenAddPartDialog(false);
  }, [setOpenAddPartDialog]);

  const minusButtonDisabled = (moveStock && tempQty === 1) || tempQty === 0;
  const addButtonDisabled = moveStock && tempQty === (selectedPart?.quantity ?? 0);

  return (
    <Dialog fullScreen open={open} onClose={handleClose} data-testid="SelectStockToSyncDialog">
      <AppBar position="fixed">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label={intl.formatMessage({ id: "general.back" })}
            size="large"
            data-testid="SelectStockToSyncDialog-CloseButton"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" marginLeft="16px" flex={1}>
            {moveStock ? (
              <FormattedMessage id="stock.moveStock" />
            ) : (
              <FormattedMessage id="stock.updateStock" />
            )}
          </Typography>
        </Toolbar>
      </AppBar>
      <Toolbar />
      <StyledContainer data-testid="SelectStockDialog">
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <PartAutocomplete
              selectedPart={selectedPart}
              partList={
                moveStock ? techStock.filter((s) => s.quantity && s.quantity > 0) : techStock
              }
              loading={loading}
              showQty
              partSearch={partSearch}
              setPartSearch={setPartSearch}
              setScannerValue={() => {}}
              changeSelectedPart={changeSelectedPart}
              disableBarcodeScan
            />
          </Grid>

          {selectedPart && (
            <Grid item container direction="row" justifyContent="space-around" alignItems="center">
              <Grid item xs={2} textAlign="center">
                <DecreaseButton
                  disabled={minusButtonDisabled}
                  className={minusButtonDisabled ? "buttonDisabled" : ""}
                  onClick={() => setTempQty(tempQty - 1)}
                />
              </Grid>
              <Grid item xs={7} textAlign="center">
                <StyledTextField
                  value={tempQty}
                  id="quantity"
                  type="number"
                  margin="normal"
                  onChange={(event) => changeQtyFromInput(event.target.value)}
                  helperText={qtyError}
                  error={qtyError !== ""}
                />
              </Grid>
              <Grid item xs={2} textAlign="center">
                <IncreaseButton
                  disabled={addButtonDisabled}
                  className={addButtonDisabled ? "buttonDisabled" : ""}
                  onClick={() => setTempQty(tempQty + 1)}
                />
              </Grid>
            </Grid>
          )}
          {selectedPart && moveStock && (
            <Grid container item>
              <Autocomplete
                fullWidth
                options={technicians}
                getOptionLabel={(option) => `${option.name}`}
                renderInput={(params) => (
                  <StyledTextField
                    {...params}
                    label={intl.formatMessage({ id: "stock.selectTechnician" })}
                  />
                )}
                value={selectedTechnician}
                data-testid="MoveStockPart-TechnicianSelect"
                onChange={(_, value) => {
                  setSelectedTechnician(value);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>
          )}

          <Grid item>
            <PrimaryButton disabled={selectedPart === null} onClick={addStockCb}>
              {moveStock ? (
                <FormattedMessage id="stock.moveStock" />
              ) : (
                <FormattedMessage id="stock.updateStock" />
              )}
            </PrimaryButton>
          </Grid>
          <Grid item>
            <SecondaryButton
              variant="contained"
              onClick={closeDialog}
              data-testid="SelectStockDialogFinish"
            >
              {intl.formatMessage({
                id: "general.finish",
              })}
            </SecondaryButton>
          </Grid>
        </Grid>
      </StyledContainer>
    </Dialog>
  );
};
